/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, IAny, ICreateActionContext, IGeneric } from '@msdyn365-commerce/core';
import { ProductDimensionAvailabilitySearchCriteria, ProductDimensionType, ProductDimensionValueInventoryAvailability, ProductsDataActions
} from '@msdyn365-commerce/retail-proxy';

import { ArrayExtensions } from './utilities';
import { createInventoryAvailabilitySearchCriteria } from './utilities/product-inventory-utils';

/* eslint-disable inclusive-language/use-inclusive-words -- disabling inclusive words for this file. */

/**
 * Interface for IProductDimension.
 */
export interface IProductDimension {
    masterProductId: number;
    requestDimensionType: ProductDimensionType;
}

/**
 * Product Input.
 */
export class ProductsDimensionAvailabilitiesInput implements IActionInput {

    public masterProductId: number;

    public requestDimensionType: ProductDimensionType;

    public constructor(masterProductId: number, requestDimensionType: ProductDimensionType) {
        this.masterProductId = masterProductId;
        this.requestDimensionType = requestDimensionType;

    }

    /**
     * Cache key.
     * @returns String.
     */
    public getCacheKey: () => string = () => `MasterProductId-${this.masterProductId.toString()}-DimensionType-${this.requestDimensionType}-Availabilities`;

    /**
     * Cache object type.
     * @returns String.
     */
    public getCacheObjectType = (): string => 'MasterProductDimensionAvailabilities';

    /**
     * Cache type.
     * @returns String.
     */
    public dataCacheType = (): CacheType => 'application';
}

/**
 * Create input method for the getDimensionsForSelectedVariant data action.
 * @param inputData - The input data passed to the createInput method.
 * @returns - The input for the data action.
 */
export const createProductsDimensionAvailabilitiesInput = (inputData: ICreateActionContext<IGeneric<IAny>>): ProductsDimensionAvailabilitiesInput[] => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- ignore type of config.
    const productsDimensions: IProductDimension[] = inputData.config?.productsDimensions;

    return productsDimensions.map((productDimension: IProductDimension) => {
        return new ProductsDimensionAvailabilitiesInput(productDimension.masterProductId, productDimension.requestDimensionType);
    });
};

/**
 * Action Method for the getDimensionsForSelectedVariant Data Action.
 * @param inputs - Input for the data action.
 * @param context - Action context.
 * @returns Master product dimensions with availabilities.
 */
export async function getDimensionAvailabilitiesForProductsAction(
    inputs: ProductsDimensionAvailabilitiesInput[],
    context: IActionContext
): Promise<ProductDimensionValueInventoryAvailability[][]> {
    if (!Array.isArray(inputs) || !ArrayExtensions.hasElements(inputs)) {
        context.trace('[getDimensionAvailabilitiesForProductsAction] Invalid or empty inputs passed.');
        return [];
    }

    const promise = inputs.map(input => {
        // As per default PDP loads shipping inventory policies, so making swatch to  pick the same configuration
        const shippingInventoryConfiguration = createInventoryAvailabilitySearchCriteria(context, [input.masterProductId], true, undefined, undefined);

        const searchCriteria: ProductDimensionAvailabilitySearchCriteria =
        {
            RequestedDimensionTypeValue: input.requestDimensionType,
            MatchingDimensionValues: undefined,
            DefaultWarehouseOnly: shippingInventoryConfiguration.DefaultWarehouseOnly,
            FilterByChannelFulfillmentGroup: shippingInventoryConfiguration.FilterByChannelFulfillmentGroup,
            DeliveryModeTypeFilterValue: shippingInventoryConfiguration.DeliveryModeTypeFilterValue
        };

        const dimensionValuesPromise = ProductsDataActions.getDimensionValuesWithEstimatedAvailabilitiesAsync(
            { callerContext: context, queryResultSettings: {} },
            input.masterProductId,
            searchCriteria
        );

        return dimensionValuesPromise;

    });

    return await Promise.all(promise);
}

/**
 * The GetDimensionAvailabilitiesForProductsAction
 * Gets the available product dimensions for the master product via the getDimensionValues RetailServer API.
 */
export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-dimension-availabilities-for-products',
    action: getDimensionAvailabilitiesForProductsAction as IAction<ProductDimensionValueInventoryAvailability[][]>,
    input: createProductsDimensionAvailabilitiesInput,
    isBatched: true
});
/* eslint-enable inclusive-language/use-inclusive-words -- enabling it back. */
